import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { MaterialIcon } from '../ui/MaterialIcon';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import type { ChecklistVariant } from '@bower/shared';

interface OnboardingChecklistStepProps {
  stepKey: string;
  label: string;
  description?: string;
  isComplete: boolean;
  completionMethod: 'derived' | 'explicit';
  onCtaClick?: () => void;
  ctaLabel?: string;
  onSkipClick?: () => void;
  /** Unified setup list variant, sent with the completion event. */
  variant?: ChecklistVariant;
}

export function OnboardingChecklistStep({
  stepKey,
  label,
  description,
  isComplete,
  completionMethod,
  onCtaClick,
  ctaLabel,
  onSkipClick,
  variant,
}: OnboardingChecklistStepProps) {
  // Track completion transitions — init ref to initial value to avoid firing on mount
  const prevIsComplete = useRef(isComplete);
  useEffect(() => {
    if (isComplete && !prevIsComplete.current) {
      safeAnalytics.track(ANALYTICS_EVENTS.ONBOARDING_CHECKLIST_STEP_COMPLETED, {
        step_key: stepKey,
        step_label: label,
        completion_method: completionMethod,
        ...(variant && { variant }),
      });
    }
    prevIsComplete.current = isComplete;
  }, [isComplete, stepKey, label, completionMethod, variant]);

  return (
    <HStack justify="space-between" py={1.5} align="flex-start">
      <HStack spacing={3} flex={1} opacity={isComplete ? 0.6 : 1} align="flex-start">
        <Box pt="2px" flexShrink={0}>
          <MaterialIcon
            name={isComplete ? 'check_circle' : 'radio_button_unchecked'}
            color={isComplete ? 'status.success' : 'text.muted'}
            boxSize="18px"
          />
        </Box>
        <VStack spacing={0} align="flex-start">
          <Text
            fontSize="sm"
            color={isComplete ? 'text.muted' : 'text.primary'}
            textDecoration={isComplete ? 'line-through' : 'none'}
          >
            {label}
          </Text>
          {description && !isComplete && (
            <Text fontSize="xs" color="text.muted" lineHeight="short">
              {description}
            </Text>
          )}
        </VStack>
      </HStack>
      {!isComplete && (
        <HStack spacing={1} flexShrink={0} pt="1px">
          {ctaLabel && onCtaClick && (
            <Button size="xs" variant="ghost" onClick={onCtaClick}>
              {ctaLabel} →
            </Button>
          )}
          {onSkipClick && (
            <Button
              size="xs"
              variant="link"
              color="text.muted"
              fontWeight="normal"
              onClick={onSkipClick}
            >
              Skip
            </Button>
          )}
        </HStack>
      )}
    </HStack>
  );
}
